import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { KitButton, KitCard, KitItem, KitList, KitPill, SecH } from '../common/MobileKit';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import {
    KitBanner,
    KitField,
    KitFormBar,
    KitGrid2,
    KitInput,
    KitKV,
    KitSearch,
    KitSelect,
    KitStepper,
} from '../common/MobileKitExtras';
import { useAppBarAction } from '../layout/AppBarActions';

/**
 * Nova compra (lançamento manual) no shell mobile — `prototipos-mobile/compras/manual.html`.
 * Só apresentação: busca de produtos, validações e envio vêm de `pages/purchases/Manual.js`.
 */

const PackageIcon = protoIcon('package');
const PlusIcon = protoIcon('plus');
const TrashIcon = protoIcon('trash');
const ArrowIcon = protoIcon('right');

const optId = (o) => o?.id ?? o?.ID ?? o?.Id ?? '';
const optName = (o) => o?.name || o?.Name || '';

const sheetRowSx = {
    display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minHeight: 56, p: '10px 14px', textAlign: 'left', border: 0,
    borderBottom: '1px solid', borderColor: 'divider', background: 'none', fontFamily: 'inherit', color: 'text.primary', cursor: 'pointer',
    '&:last-child': { borderBottom: 0 }, '&:active': { backgroundColor: 'background.subtle' },
};

export default function MobilePurchaseManual({
    suppliers, supplierId, onSupplier,
    dueDate, onDueDate, reference, onReference,
    items, money, toBRL, toCents,
    catName, subName, stockOf,
    query, onQueryChange, q, matches, exact,
    mode, selected,
    qty, onQty, qtyError, cost, onCost, costError,
    form, onForm, showErrors, newErrors, categories, subcategories,
    selectExisting, startNew, resetEntry, addFound, addNew, generateCode,
    setItemQty, setItemCost, removeItem, clearItems,
    totalCents, submitting, onFinish, onCancel,
}) {
    const [addOpen, setAddOpen] = useState(false);
    const [editKey, setEditKey] = useState(null);
    const prevItems = useRef(items);

    useAppBarAction({
        enabled: true,
        menu: items.length ? [{ label: 'Limpar lista', icon: TrashIcon, destructive: true, disabled: submitting, onClick: clearItems }] : [],
    });

    // Item adicionado (ou somado) → fecha a folha de adicionar.
    useEffect(() => {
        if (addOpen && prevItems.current !== items && mode === 'idle') setAddOpen(false);
        prevItems.current = items;
    }, [items, addOpen, mode]);

    const closeAdd = () => { setAddOpen(false); resetEntry(); };
    const editing = items.find((i) => i.key === editKey);
    const found = mode === 'found' && selected;
    const qtyNum = Math.max(0, parseInt(qty, 10) || 0);
    const curStock = found ? stockOf(selected) : 0;

    let footer;
    if (found) {
        footer = (
            <>
                <KitButton sx={{ flex: 1 }} onClick={resetEntry}>Trocar produto</KitButton>
                <KitButton variant="primary" sx={{ flex: 1 }} onClick={addFound}>Adicionar</KitButton>
            </>
        );
    } else if (mode === 'new') {
        footer = (
            <>
                <KitButton sx={{ flex: 1 }} onClick={resetEntry}>Cancelar cadastro</KitButton>
                <KitButton variant="primary" sx={{ flex: 1 }} onClick={addNew}>Cadastrar e adicionar</KitButton>
            </>
        );
    }

    return (
        <Box sx={{ fontSize: 14, lineHeight: 1.4 }}>
            <KitField label="Fornecedor (opcional)">
                <KitSelect value={supplierId} onChange={(e) => onSupplier(e.target.value)} aria-label="Fornecedor">
                    <option value="">Selecione</option>
                    {suppliers.map((s) => <option key={optId(s)} value={optId(s)}>{optName(s)}</option>)}
                </KitSelect>
            </KitField>
            <KitGrid2>
                <KitField label="Vencimento">
                    <KitInput type="date" value={dueDate} onChange={(e) => onDueDate(e.target.value)} aria-label="Vencimento" />
                </KitField>
                <KitField label="Referência">
                    <KitInput type="text" value={reference} onChange={(e) => onReference(e.target.value)} placeholder="NF 12345" aria-label="Referência" />
                </KitField>
            </KitGrid2>

            <SecH title={`Itens (${items.length})`} />
            <KitList>
                {items.length === 0 && (
                    <Box sx={{ p: '24px 16px', textAlign: 'center', fontSize: 13, color: 'text.disabled' }}>Nenhum item adicionado ainda.</Box>
                )}
                {items.map((it) => (
                    <KitItem
                        key={it.key}
                        icon={PackageIcon}
                        tone={it.kind === 'new' ? 'purple' : undefined}
                        title={it.p.name}
                        sub={it.error ? <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>{it.error}</Box> : `${it.qty} × R$ ${it.cost || '—'}`}
                        end={<Box sx={{ fontWeight: 700, fontSize: 14, ...(toBRL(it.cost) ? {} : { color: 'error.main' }) }}>{money(toCents(it.cost) * it.qty)}</Box>}
                        onClick={() => setEditKey(it.key)}
                    />
                ))}
            </KitList>

            <KitButton variant="soft" block sx={{ mt: '12px' }} onClick={() => setAddOpen(true)}>
                <PlusIcon size={18} /> Adicionar item
            </KitButton>

            <KitCard sx={{ mt: '12px' }}>
                <KitKV big totals label="Total" value={money(totalCents)} sx={{ borderTop: 0, mt: 0, p: 0 }} />
            </KitCard>

            <KitFormBar>
                <KitButton disabled={submitting} onClick={onCancel}>Cancelar</KitButton>
                <KitButton variant="primary" disabled={!items.length || submitting} onClick={onFinish}>
                    {submitting ? 'Lançando...' : 'Registrar compra'}
                </KitButton>
            </KitFormBar>

            {/* ===== Adicionar item ===== */}
            <MobileSheet open={addOpen} onClose={closeAdd} title="Adicionar item" footer={footer}>
                {mode === 'idle' && (
                    <>
                        <Box sx={{ display: 'flex', mb: '12px' }}>
                            <KitSearch value={query} onChange={onQueryChange} placeholder="Nome ou código do produto…" inputProps={{ 'aria-label': 'Nome ou código do produto', autoComplete: 'off' }} />
                        </Box>
                        {!q && <Box sx={{ fontSize: 13, color: 'text.disabled', p: '4px 4px 8px' }}>Se o produto já existir, o estoque será atualizado. Se não existir, você poderá cadastrá-lo aqui mesmo.</Box>}
                        {q && (
                            <KitList>
                                {matches.map((p) => (
                                    <Box key={p.id} component="button" type="button" onClick={() => selectExisting(p)} sx={sheetRowSx}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: '12px', flexShrink: 0, backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PackageIcon size={20} /></Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ fontWeight: 700, fontSize: 14 }}>{p.name}</Box>
                                            <Box sx={{ fontSize: 12, color: 'text.disabled' }}>Cód. {p.code}{catName(p) ? ` · ${catName(p)}` : ''}{subName(p) ? ` › ${subName(p)}` : ''}</Box>
                                        </Box>
                                        <Box sx={{ textAlign: 'right', fontSize: 11, color: 'text.disabled', flexShrink: 0 }}>
                                            <Box component="b" sx={{ display: 'block', fontSize: 12.5, color: 'text.primary' }}>{p.quantity || 0} un</Box>em estoque
                                        </Box>
                                    </Box>
                                ))}
                                {matches.length === 0 && <Box sx={{ p: '14px', fontSize: 13, color: 'text.secondary' }}>Nenhum produto encontrado para <b>“{q}”</b>.</Box>}
                                {!exact && (
                                    <Box component="button" type="button" onClick={() => startNew(q)} sx={sheetRowSx}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: '12px', flexShrink: 0, backgroundColor: 'secondary.bg', color: 'secondary.main', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><PlusIcon size={20} /></Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ fontWeight: 700, fontSize: 14, color: 'secondary.main' }}>Cadastrar novo produto “{q}”</Box>
                                            <Box sx={{ fontSize: 12, color: 'text.disabled' }}>Defina código, categoria e subcategoria</Box>
                                        </Box>
                                    </Box>
                                )}
                            </KitList>
                        )}
                    </>
                )}

                {found && (
                    <>
                        <KitCard sx={{ p: '14px', mb: '14px' }}>
                            <Box sx={{ fontWeight: 800, fontSize: 14.5 }}>{selected.name}</Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: 12, color: 'text.disabled', mt: '4px' }}>
                                <KitPill tone="info">Existente</KitPill>
                                <span>Cód. <b>{selected.code}</b></span>
                                {catName(selected) && <span>· {catName(selected)}{subName(selected) ? ` › ${subName(selected)}` : ''}</span>}
                            </Box>
                            <Box aria-live="polite" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', mt: '12px', p: '10px', borderRadius: '12px', backgroundColor: 'background.subtle' }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled' }}>Estoque atual</Box>
                                    <Box component="b" sx={{ fontSize: 20, fontWeight: 800 }}>{curStock}</Box>
                                </Box>
                                <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'success.main', backgroundColor: 'success.bg', p: '3px 8px', borderRadius: '20px' }}>+ {qtyNum}</Box>
                                <Box sx={{ color: 'text.disabled', display: 'flex' }}><ArrowIcon size={18} /></Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Box component="span" sx={{ display: 'block', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled' }}>Após a compra</Box>
                                    <Box component="b" sx={{ fontSize: 20, fontWeight: 800, color: 'success.main' }}>{curStock + qtyNum}</Box>
                                </Box>
                            </Box>
                        </KitCard>
                        <KitGrid2>
                            <KitField label="Quantidade" required help={qtyError ? 'Informe uma quantidade maior que 0.' : undefined} error={qtyError}>
                                <KitInput type="number" inputMode="numeric" min={1} step={1} value={qty} error={qtyError} onChange={(e) => onQty(e.target.value)} aria-label="Quantidade" />
                            </KitField>
                            <KitField label="Custo unitário (R$)" required help={costError ? 'Informe o custo (maior que 0).' : undefined} error={costError}>
                                <KitInput inputMode="decimal" value={cost} error={costError} onChange={(e) => onCost(e.target.value)} aria-label="Custo unitário" />
                            </KitField>
                        </KitGrid2>
                    </>
                )}

                {mode === 'new' && (
                    <>
                        <Box sx={{ mb: '14px' }}>
                            <KitBanner tone="info">Não encontrado no cadastro — será criado ao finalizar a compra.</KitBanner>
                        </Box>
                        <KitField label="Nome do produto" required help={showErrors && newErrors.name ? 'Informe o nome do produto.' : undefined} error>
                            <KitInput value={form.name} error={showErrors && newErrors.name} onChange={(e) => onForm({ name: e.target.value })} aria-label="Nome do produto" />
                        </KitField>
                        <KitField label="Código" required help={showErrors && newErrors.code ? newErrors.codeMsg : undefined} error>
                            <KitInput value={form.code} error={showErrors && newErrors.code} onChange={(e) => onForm({ code: e.target.value })} aria-label="Código" />
                            <Box component="button" type="button" onClick={generateCode} sx={{ border: 0, background: 'none', color: 'primary.main', fontWeight: 700, fontSize: 12.5, p: '10px 2px 0', cursor: 'pointer', fontFamily: 'inherit' }}>Gerar automaticamente</Box>
                        </KitField>
                        <KitField label="Categoria" required help={showErrors && newErrors.category ? 'Selecione a categoria.' : undefined} error>
                            <KitSelect value={form.category} onChange={(e) => onForm({ category: e.target.value, subcategory: '' })} aria-label="Categoria" sx={showErrors && newErrors.category ? { borderColor: 'error.main' } : undefined}>
                                <option value="">Selecione</option>
                                {categories.map((c) => <option key={optId(c)} value={optId(c)}>{optName(c)}</option>)}
                            </KitSelect>
                        </KitField>
                        <KitField label="Subcategoria" required help={showErrors && newErrors.subcategory ? 'Selecione a subcategoria.' : undefined} error>
                            <KitSelect value={form.subcategory} disabled={!form.category} onChange={(e) => onForm({ subcategory: e.target.value })} aria-label="Subcategoria" sx={showErrors && newErrors.subcategory ? { borderColor: 'error.main' } : undefined}>
                                <option value="">Selecione</option>
                                {subcategories.map((c) => <option key={optId(c)} value={optId(c)}>{optName(c)}</option>)}
                            </KitSelect>
                        </KitField>
                        <KitGrid2>
                            <KitField label="Qtd. comprada" required help={showErrors && newErrors.qty ? 'Mínimo 1.' : undefined} error>
                                <KitInput type="number" inputMode="numeric" min={1} step={1} value={form.qty} error={showErrors && newErrors.qty} onChange={(e) => onForm({ qty: e.target.value })} aria-label="Quantidade comprada" />
                            </KitField>
                            <KitField label="Custo unit. (R$)" required help={showErrors && newErrors.cost ? 'Informe o custo (maior que 0).' : 'Forma o preço de venda com a margem da categoria.'} error={showErrors && newErrors.cost}>
                                <KitInput inputMode="decimal" value={form.cost} error={showErrors && newErrors.cost} onChange={(e) => onForm({ cost: e.target.value })} aria-label="Custo unitário" />
                            </KitField>
                        </KitGrid2>
                    </>
                )}
            </MobileSheet>

            {/* ===== Editar item ===== */}
            <MobileSheet
                open={Boolean(editing)}
                onClose={() => setEditKey(null)}
                title={editing ? editing.p.name : 'Item'}
                footer={(
                    <>
                        <KitButton variant="danger" sx={{ flex: 1 }} onClick={() => { removeItem(editKey); setEditKey(null); }}>Remover</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={() => setEditKey(null)}>Concluir</KitButton>
                    </>
                )}
            >
                {editing && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', fontSize: 12, color: 'text.disabled', mb: '14px' }}>
                            <KitPill tone={editing.kind === 'new' ? 'purple' : 'info'}>{editing.kind === 'new' ? 'Novo produto' : 'Existente'}</KitPill>
                            <span>Cód. {editing.p.code}</span>
                        </Box>
                        {editing.error && <Box sx={{ mb: '14px' }}><KitBanner tone="danger">{editing.error}</KitBanner></Box>}
                        <KitField label="Quantidade">
                            <KitStepper value={editing.qty} onMinus={() => setItemQty(editing.key, editing.qty - 1)} onPlus={() => setItemQty(editing.key, editing.qty + 1)} minusDisabled={editing.qty <= 1} />
                        </KitField>
                        <KitField label="Custo unitário (R$)" error={!toBRL(editing.cost)} help={toBRL(editing.cost) ? undefined : 'Informe o custo unitário (maior que 0).'}>
                            <KitInput inputMode="decimal" value={editing.cost || ''} error={!toBRL(editing.cost)} onChange={(e) => setItemCost(editing.key, e.target.value)} aria-label="Custo unitário" />
                        </KitField>
                        <KitKV label="Subtotal" value={money(toCents(editing.cost) * editing.qty)} />
                    </>
                )}
            </MobileSheet>
        </Box>
    );
}
